import type {
  PublicBanner,
  PublicBannerSection,
} from '@/common/types/banner-section.type';
import type {
  HomeCarouselBanner,
  HomeCarouselItem,
  HomeCarouselProduct,
  HomeCarouselType,
  HomeCarouselVendor,
} from '@/common/types/home-carousel.type';
import type { LandingCategoryType } from '@/common/types/landingCategory.type';
import type { RecommendationProduct } from '@/common/types/recommended-products';
import { appConfigs } from '@/common/configs/app.config';
import { rialToToman } from '@/common/utils/money-exchange.utility';
import { mapProductPricing } from '@/common/utils/product-pricing.utility';
import {
  resolveProductStorefrontHref,
  resolveVendorStorefrontHref,
} from '@/common/utils/tenant-href.utility';
import {
  createPlpHref,
  defaultPlpUrlState,
} from '@/features/shop-plp/data/plp-url-state';

export const FALLBACK_PRODUCT_IMAGE =
  '/assets/images/svg/image-placeholder.svg';
const DEFAULT_BANNER_DURATION = 5000;

export function toSecureMediaUrl(url?: string | null) {
  if (!url) return '';
  return url.startsWith('http://') ? `https://${url.slice(7)}` : url;
}

export interface ShopCarouselProduct {
  id: string;
  image: string;
  name: string;
  store: string;
  price: number;
  originalPrice?: number;
  discount?: number;
  href?: string;
}

export interface ShopHomeCategory {
  id: number;
  title: string;
  slug: string;
}

export interface ShopMainBannerSlide {
  imageUrlDesktop: string;
  imageUrlMobile: string;
  link: string;
  alt?: string;
}

export interface ShopTopBannerItem {
  imageUrlDesktop: string;
  imageUrlMobile: string;
  href: string;
  alt?: string;
  duration: number;
}

export interface ShopNewestStore {
  id: number;
  name: string;
  description: string;
  logo: string;
  href?: string;
  products: Array<{ id: string; image: string; alt?: string }>;
}

export interface ShopProductCarouselSection {
  type: HomeCarouselType;
  title: string;
  products: ShopCarouselProduct[];
  viewAllHref: string;
}

export interface ShopBannerCarouselSection extends ShopProductCarouselSection {
  desktopBanner: string;
  mobileBanner: string;
  bannerAlt: string;
  bannerHref?: string;
}

/**
 * Local icon keys for the 12 Witro root categories from `/v1/Category/All`
 * (ids + titles + backend slugs verified against live API).
 */
const CATEGORY_SLUG = {
  groceries: 'groceries',
  clothes: 'clothes',
  homeDecoration: 'home-decoration',
  handicrafts: 'handicrafts',
  health: 'health',
  makeup: 'makeup',
  learning: 'learning',
  sports: 'sports',
  service: 'service',
  digitalGoods: 'digital-goods',
  tools: 'tools',
  other: 'other',
} as const;

const CATEGORY_SLUG_BY_ID: Record<number, string> = {
  1: CATEGORY_SLUG.groceries,
  2: CATEGORY_SLUG.clothes,
  3: CATEGORY_SLUG.homeDecoration,
  4: CATEGORY_SLUG.handicrafts,
  5: CATEGORY_SLUG.health,
  6: CATEGORY_SLUG.makeup,
  7: CATEGORY_SLUG.learning,
  8: CATEGORY_SLUG.sports,
  9: CATEGORY_SLUG.service,
  10: CATEGORY_SLUG.digitalGoods,
  11: CATEGORY_SLUG.tools,
  3665: CATEGORY_SLUG.other,
};

const CATEGORY_SLUG_BY_TITLE: Record<string, string> = {
  'مواد غذایی': CATEGORY_SLUG.groceries,
  موادغذایی: CATEGORY_SLUG.groceries,
  'مد و پوشاک': CATEGORY_SLUG.clothes,
  'خانه و آشپزخانه': CATEGORY_SLUG.homeDecoration,
  'صنایع دستی': CATEGORY_SLUG.handicrafts,
  سلامت: CATEGORY_SLUG.health,
  'درمان و سلامت': CATEGORY_SLUG.health,
  'آرایشی و بهداشتی': CATEGORY_SLUG.makeup,
  آموزش: CATEGORY_SLUG.learning,
  'فرهنگی و آموزشی': CATEGORY_SLUG.learning,
  'ورزش و سفر': CATEGORY_SLUG.sports,
  خدمات: CATEGORY_SLUG.service,
  'خدمات کسب و کار': CATEGORY_SLUG.service,
  'خدمات و کسب و کار': CATEGORY_SLUG.service,
  'کالای دیجیتال': CATEGORY_SLUG.digitalGoods,
  'ابزار و لوازم یدکی': CATEGORY_SLUG.tools,
  'ابزارآلات و لوازم یدکی': CATEGORY_SLUG.tools,
  سایر: CATEGORY_SLUG.other,
};

/** Backend `/Category` slugs → local icon keys (when title/id resolution is unavailable). */
export const CATEGORY_ICON_KEY_BY_BACKEND_SLUG: Record<string, string> = {
  'مواد-غذایی-کد(793141)': CATEGORY_SLUG.groceries,
  apparel: CATEGORY_SLUG.clothes,
  'home-and-kitchen': CATEGORY_SLUG.homeDecoration,
  handicrafts: CATEGORY_SLUG.handicrafts,
  'health-treatment': CATEGORY_SLUG.health,
  'beauty-and-personal-care': CATEGORY_SLUG.makeup,
  'cultural-educational': CATEGORY_SLUG.learning,
  'sport-travel': CATEGORY_SLUG.sports,
  services: CATEGORY_SLUG.service,
  'electronic-devices': CATEGORY_SLUG.digitalGoods,
  'Automotive-tools-equipment': CATEGORY_SLUG.tools,
  other: CATEGORY_SLUG.other,
};

export function resolveCategorySlug(
  categoryId: number,
  categoryTitle?: string,
  backendSlug?: string
) {
  const normalizedTitle = categoryTitle?.trim() ?? '';
  const normalizedBackendSlug = backendSlug?.trim() ?? '';

  return (
    CATEGORY_SLUG_BY_TITLE[normalizedTitle] ??
    CATEGORY_SLUG_BY_ID[categoryId] ??
    CATEGORY_ICON_KEY_BY_BACKEND_SLUG[normalizedBackendSlug] ??
    (normalizedBackendSlug || CATEGORY_SLUG.other)
  );
}

export function findHomeCarousel(
  items: HomeCarouselItem[],
  type: HomeCarouselType
) {
  return items.find((item) => item.type === type);
}

export function findBannerSection(
  sections: PublicBannerSection[],
  type: PublicBannerSection['type']
) {
  return sections.find((section) => section.type === type);
}

export function createCarouselPlpHref(type: HomeCarouselType) {
  return createPlpHref({
    ...defaultPlpUrlState,
    carousel: type,
  });
}

function toFiniteNumber(value: number | string | null | undefined) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isValidPrice(value: number | string | null | undefined) {
  return value !== null && value !== undefined && Number.isFinite(Number(value)) && Number(value) >= 0;
}

function sortByOrder<T extends { displayOrder?: number; sortOrder?: number }>(
  items: T[]
) {
  return [...items].sort(
    (first, second) =>
      (first.displayOrder ?? first.sortOrder ?? 0) -
      (second.displayOrder ?? second.sortOrder ?? 0)
  );
}

export function toShopperProductHref(product: HomeCarouselProduct) {
  return resolveProductStorefrontHref({
    domain: product.domain,
    vendorSlug: product.vendorSlug,
    productId: product.productId,
    productSlug: product.productSlug,
    fallbackUrl: product.productUrl,
  });
}

export function mapHomeCarouselProduct(
  product: HomeCarouselProduct
): ShopCarouselProduct {
  const rawFinalPrice = product.finalPrice ?? product.price;
  const rawOriginalPrice = product.oldPrice ?? product.price;
  const price = rialToToman(
    Math.max(0, toFiniteNumber(rawFinalPrice)),
    appConfigs.rialExchangeRate
  );
  const originalPrice = rialToToman(
    Math.max(0, toFiniteNumber(rawOriginalPrice)),
    appConfigs.rialExchangeRate
  );
  const discount = Math.round(toFiniteNumber(product.discountPercent));
  const hasDiscount =
    discount > 0 &&
    discount <= 100 &&
    isValidPrice(rawFinalPrice) &&
    isValidPrice(rawOriginalPrice) &&
    originalPrice > price;

  return {
    id: String(product.productId),
    image: toSecureMediaUrl(product.thumbnailImage) || FALLBACK_PRODUCT_IMAGE,
    name: product.productName,
    store: product.vendorName,
    price,
    href: toShopperProductHref(product),
    ...(hasDiscount ? { originalPrice, discount } : {}),
  };
}

export function mapHomeCarouselProducts(products: HomeCarouselProduct[] = []) {
  return sortByOrder(products).map(mapHomeCarouselProduct);
}

export function mapRecommendationProduct(
  product: RecommendationProduct
): ShopCarouselProduct {
  const pricing = mapProductPricing({
    price: product.price,
    discount: product.discount,
    oldPrice: product.oldPrice,
    finalPrice: product.finalPrice,
    discountPercent: product.discountPercent,
  });

  return {
    id: String(product.productId),
    image: toSecureMediaUrl(product.thumbnailImage) || FALLBACK_PRODUCT_IMAGE,
    name: product.productTitle,
    store: product.vendorName,
    ...pricing,
    href: resolveProductStorefrontHref({
      domain: product.domain,
      vendorSlug: product.vendorSlug,
      productId: product.productId,
      productSlug: product.slug,
    }),
  };
}

export function getWitroshopRecommendationProducts(
  response:
    | {
        data?: { recommendedProducts?: RecommendationProduct[] | null } | null;
        recommendedProducts?: RecommendationProduct[] | null;
      }
    | null
    | undefined
): RecommendationProduct[] | null {
  if (!response) return null;

  const products =
    response.data?.recommendedProducts ?? response.recommendedProducts ?? null;

  return products?.length ? products : null;
}

export function mapTopBanners(
  section: PublicBannerSection | undefined
): ShopTopBannerItem[] {
  if (!section) return [];

  const duration = section.autoPlay
    ? section.slideIntervalMilliseconds || DEFAULT_BANNER_DURATION
    : 86_400_000;

  return sortByOrder(section.banners).flatMap((banner) => {
    const item = mapMediaBanner(banner, duration);
    return item ? [item] : [];
  });
}

export function mapMainBannerSlides(section: PublicBannerSection | undefined): {
  slides: ShopMainBannerSlide[];
  duration: number;
} {
  const duration =
    section?.slideIntervalMilliseconds || DEFAULT_BANNER_DURATION;
  if (!section) return { slides: [], duration };

  return {
    duration,
    slides: sortByOrder(section.banners).flatMap((banner) => {
      const media = pickBannerMedia(banner);
      if (!media) return [];
      return [
        {
          imageUrlDesktop: media.desktop,
          imageUrlMobile: media.mobile,
          link: banner.link || '#',
          alt: banner.title,
        },
      ];
    }),
  };
}

function pickBannerMedia(banner: {
  desktopMediaFileUrl?: string | null;
  mobileMediaFileUrl?: string | null;
}) {
  const desktop = toSecureMediaUrl(
    banner.desktopMediaFileUrl || banner.mobileMediaFileUrl || ''
  );
  const mobile = toSecureMediaUrl(
    banner.mobileMediaFileUrl || banner.desktopMediaFileUrl || ''
  );

  if (!desktop && !mobile) return undefined;

  return { desktop, mobile };
}

function mapMediaBanner(
  banner: PublicBanner | HomeCarouselBanner,
  duration: number
): ShopTopBannerItem | undefined {
  const media = pickBannerMedia(banner);
  if (!media || !banner.link) {
    return undefined;
  }

  return {
    href: banner.link,
    imageUrlDesktop: media.desktop,
    imageUrlMobile: media.mobile,
    alt: banner.title,
    duration,
  };
}

export function mapLandingCategories(
  categories: LandingCategoryType[] = []
): ShopHomeCategory[] {
  return categories.map((category) => ({
    id: category.categoryId,
    title: category.name,
    slug: resolveCategorySlug(
      category.categoryId,
      category.name,
      category.slug
    ),
  }));
}

export function resolveHomeCarouselHeading(
  carousel:
    | Pick<HomeCarouselItem, 'title' | 'listTitle'>
    | null
    | undefined
): string | undefined {
  const listTitle = carousel?.listTitle?.trim();
  if (listTitle) return listTitle;

  const title = carousel?.title?.trim();
  return title || undefined;
}

export function mapProductCarouselSection(
  carousel: HomeCarouselItem | undefined
): ShopProductCarouselSection | undefined {
  if (!carousel?.products?.length) return undefined;

  return {
    type: carousel.type,
    title: resolveHomeCarouselHeading(carousel) ?? carousel.title,
    products: mapHomeCarouselProducts(carousel.products),
    viewAllHref: createCarouselPlpHref(carousel.type),
  };
}

export function mapBannerCarouselSection(
  carousel: HomeCarouselItem | undefined
): ShopBannerCarouselSection | undefined {
  const products = mapProductCarouselSection(carousel);
  if (!products || !carousel) return undefined;

  const banner = sortByOrder(carousel.banners ?? [])[0];
  const media = banner ? pickBannerMedia(banner) : undefined;
  if (!media) {
    return undefined;
  }

  return {
    ...products,
    desktopBanner: media.desktop,
    mobileBanner: media.mobile,
    bannerAlt: banner?.title || products.title,
    bannerHref: banner.link || products.viewAllHref,
  };
}

export function mapNewestStores(
  vendors: HomeCarouselVendor[] = [],
  viewAllHref: string
): { stores: ShopNewestStore[]; viewAllHref: string } {
  return {
    viewAllHref,
    stores: vendors.map((vendor) => ({
      id: vendor.vendorId,
      name: vendor.vendorName,
      description:
        vendor.aboutMe?.trim() ||
        vendor.shortDescription?.trim() ||
        'فروشگاه جدید در ویترو',
      logo:
        toSecureMediaUrl(vendor.vendorLogo || vendor.thumbnailImage) ||
        FALLBACK_PRODUCT_IMAGE,
      href: resolveVendorStorefrontHref({
        domain: vendor.domain,
        vendorSlug: vendor.vendorSlug,
        fallbackUrl: vendor.vendorUrl,
      }),
      products: (vendor.products ?? [])
        .filter((product) => Boolean(product.thumbnailImage))
        .slice(0, 3)
        .map((product) => ({
          id: String(product.productId),
          image: toSecureMediaUrl(product.thumbnailImage),
          alt: product.productName || vendor.vendorName,
        })),
    })),
  };
}

export interface ShopHomeData {
  topBanners: ShopTopBannerItem[];
  mainBanner: {
    slides: ShopMainBannerSlide[];
    duration: number;
  };
  categories: ShopHomeCategory[];
  specialOffer?: ShopProductCarouselSection;
  selectedProducts?: ShopProductCarouselSection;
  vitrinOff?: ShopProductCarouselSection;
  bannerCarouselOne?: ShopBannerCarouselSection;
  bannerCarouselTwo?: ShopBannerCarouselSection;
  bannerCarouselThree?: ShopBannerCarouselSection;
  newestStores: ShopNewestStore[];
  newestStoresViewAllHref: string;
  latestProducts: ShopCarouselProduct[];
  latestProductsTitle: string;
  latestProductsViewAllHref: string;
}

export function mapShopHomeData(
  sections: PublicBannerSection[] = [],
  carousels: HomeCarouselItem[] = [],
  categories: LandingCategoryType[] = [],
  newVendors: HomeCarouselVendor[] = []
): ShopHomeData {
  const carouselNewVendors = findHomeCarousel(carousels, 'NewVendors');
  const newProducts = findHomeCarousel(carousels, 'NewProducts');
  const newestStores = mapNewestStores(
    newVendors.length > 0
      ? newVendors
      : (carouselNewVendors?.vendors ?? []),
    createCarouselPlpHref('NewVendors')
  );

  return {
    topBanners: mapTopBanners(findBannerSection(sections, 'LandingTopStrip')),
    mainBanner: mapMainBannerSlides(
      findBannerSection(sections, 'LandingMainHero')
    ),
    categories: mapLandingCategories(categories),
    specialOffer: mapProductCarouselSection(
      findHomeCarousel(carousels, 'SpecialOffer')
    ),
    selectedProducts: mapProductCarouselSection(
      findHomeCarousel(carousels, 'SelectedProducts')
    ),
    vitrinOff: mapProductCarouselSection(
      findHomeCarousel(carousels, 'VitrinOff')
    ),
    bannerCarouselOne: mapBannerCarouselSection(
      findHomeCarousel(carousels, 'BannerCarouselOne')
    ),
    bannerCarouselTwo: mapBannerCarouselSection(
      findHomeCarousel(carousels, 'BannerCarouselTwo')
    ),
    bannerCarouselThree: mapBannerCarouselSection(
      findHomeCarousel(carousels, 'BannerCarouselThree')
    ),
    newestStores: newestStores.stores,
    newestStoresViewAllHref: newestStores.viewAllHref,
    latestProducts: mapHomeCarouselProducts(newProducts?.products ?? []),
    latestProductsTitle: newProducts?.title || 'جدیدترین محصولات',
    latestProductsViewAllHref: createCarouselPlpHref('NewProducts'),
  };
}
